import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from '@ludus/ui'

interface AnomaliesPanelProps {
  auditId: string
  byKind: Map<string, string[]>
}

/** Shown whatever the count (ruling 2026-08-22). */
const ALWAYS = ['call_only_lead'] as const

/**
 * Anomaly counters per `review_queue.kind`, apart from coverage by design.
 * Each links into the chat with a question carrying the queue ids.
 */
export function AnomaliesPanel({ auditId, byKind }: AnomaliesPanelProps) {
  const { t } = useTranslation()
  const kinds = [...new Set([...ALWAYS, ...byKind.keys()])].sort()
  return (
    <Card
      aria-label={t('pages.audits.anomalies.title', { defaultValue: 'Anomalies' })}
      role="region"
    >
      <CardHeader>
        <CardTitle>{t('pages.audits.anomalies.title', { defaultValue: 'Anomalies' })}</CardTitle>
        <CardDescription>
          {t('pages.audits.anomalies.description', {
            defaultValue: 'Doubts the passes filed for a person. Ask the chat about any of them.',
          })}
        </CardDescription>
      </CardHeader>
      <CardBody>
        <ul className="flex flex-col gap-1 text-sm">
          {kinds.map((kind) => {
            const ids = byKind.get(kind) ?? []
            const question = t('pages.audits.anomalies.question', {
              defaultValue: 'Explain the {{kind}} items {{ids}}',
              kind: t(`pages.audits.anomalies.kinds.${kind}`, { defaultValue: kind }),
              ids: ids.slice(0, 20).join(', '),
            })
            return (
              <li key={kind} className="flex items-center justify-between gap-3">
                <Link
                  className="text-foreground hover:underline"
                  to={`/audits/${auditId}/chat?ask=${encodeURIComponent(question)}&queue=${encodeURIComponent(ids.join(','))}`}
                >
                  {t(`pages.audits.anomalies.kinds.${kind}`, { defaultValue: kind })}
                </Link>
                <span className="font-medium">{ids.length}</span>
              </li>
            )
          })}
        </ul>
      </CardBody>
    </Card>
  )
}
